import React, { useState } from "react";
import axios from "axios";
import "./usherList.css";
import useUshers from "../../helper/useUshers";

const UsherList = ({ eventId, searchTerm: externalSearchTerm }) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsher, setSelectedUsher] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [activeUsher, setActiveUsher] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, confirmed, rejected, pending
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const effectiveSearchTerm =
    externalSearchTerm !== undefined ? externalSearchTerm : searchTerm;

  const { ushers, loading, error, setUshers } = useUshers(eventId);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  const filteredUshers = ushers.filter((usher) => {
    let matchesSearch = true;
    if (effectiveSearchTerm) {
      const searchLower = effectiveSearchTerm.toLowerCase();
      const name = usher.displayName?.toLowerCase() || "";
      const phone = usher.phoneNumber?.toLowerCase() || "";
      const email = usher.email?.toLowerCase() || "";

      matchesSearch =
        name.includes(searchLower) ||
        phone.includes(searchLower) ||
        email.includes(searchLower);
    }

    let matchesStatus = true;
    if (filterStatus !== "all") {
      matchesStatus = usher.approvalStatus === filterStatus;
    }

    return matchesSearch && matchesStatus;
  });

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentUshers = filteredUshers.slice(indexOfFirst, indexOfLast);

  const DropdownArrowIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 10l5 5 5-5z" fill="currentColor" />
    </svg>
  );

  const FilterIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"
        fill="currentColor"
      />
    </svg>
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1)
      setCurrentPage(currentPage - 1);
    else if (direction === "next" && indexOfLast < filteredUshers.length)
      setCurrentPage(currentPage + 1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleUsherApproval = async (usherId, action) => {
    if (!eventId) return;

    try {
      const response = await axios.post(
        `/checkin/event/${eventId}/usher-approval`,
        { usherId, action }
      );

      if (response.data.successStatus) {
        setUshers((prev) =>
          prev.map((u) =>
            u.usherId === usherId
              ? {
                  ...u,
                  approvalStatus:
                    action === "confirm" ? "confirmed" : "rejected",
                }
              : u
          )
        );
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleConfirmYes = () => {
    handleUsherApproval(activeUsher.usherId, "confirm");
    setShowConfirmModal(false);
  };

  const handleRejectYes = () => {
    handleUsherApproval(activeUsher.usherId, "reject");
    setShowRejectModal(false);
  };

  const getFilterLabel = () => {
    switch (filterStatus) {
      case "confirmed":
        return "Confirmed";
      case "rejected":
        return "Rejected";
      case "pending":
        return "Pending";
      default:
        return "All Status";
    }
  };

  const getFilterCount = (status) => {
    if (status === "all") return ushers.length;
    return ushers.filter((u) => u.approvalStatus === status).length;
  };

  if (loading)
    return <div className="usher-list-loading">Loading ushers...</div>;
  if (error) return <div className="usher-list-error">{error}</div>;

  return (
    <div className="usher-list-container">
      <div className="usher-list-filter-bar">
        <div className="usher-list-search-container">
          <input
            type="text"
            className="usher-list-search-input"
            placeholder="Search ushers..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <span className="usher-list-search-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </span>
        </div>

        <div className="usher-list-filter-dropdown-container">
          <button
            className="usher-list-filter-button"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <FilterIcon />
            <span>{getFilterLabel()}</span>
            <DropdownArrowIcon />
          </button>

          {showFilterDropdown && (
            <div className="usher-list-filter-dropdown">
              <div
                className={`usher-list-filter-option ${
                  filterStatus === "all" ? "active" : ""
                }`}
                onClick={() => handleFilterChange("all")}
              >
                <span>All Status</span>
                <span className="usher-list-filter-count">
                  {getFilterCount("all")}
                </span>
              </div>
              <div
                className={`usher-list-filter-option ${
                  filterStatus === "confirmed" ? "active" : ""
                }`}
                onClick={() => handleFilterChange("confirmed")}
              >
                <span>Confirmed</span>
                <span className="usher-list-filter-count usher-list-confirmed-count">
                  {getFilterCount("confirmed")}
                </span>
              </div>
              <div
                className={`usher-list-filter-option ${
                  filterStatus === "pending" ? "active" : ""
                }`}
                onClick={() => handleFilterChange("pending")}
              >
                <span>Pending</span>
                <span className="usher-list-filter-count usher-list-pending-count">
                  {getFilterCount("pending")}
                </span>
              </div>
              <div
                className={`usher-list-filter-option ${
                  filterStatus === "rejected" ? "active" : ""
                }`}
                onClick={() => handleFilterChange("rejected")}
              >
                <span>Rejected</span>
                <span className="usher-list-filter-count usher-list-rejected-count">
                  {getFilterCount("rejected")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="usher-list-list">
        {currentUshers.length === 0 ? (
          <div className="usher-list-no-ushers-message">
            {effectiveSearchTerm || filterStatus !== "all"
              ? "No ushers found matching your criteria."
              : "No ushers found."}
          </div>
        ) : (
          currentUshers.map((usher) => (
            <div
              key={usher._id}
              className="usher-list-item"
              onClick={() => setSelectedUsher(usher)}
              style={{ cursor: "pointer" }}
            >
              <div className="usher-list-info">
                <div className="usher-list-avatar">
                  <img
                    src={usher.userPhoto || "/images/avatar.webp"}
                    alt={usher.displayName || "Usher"}
                    onError={(e) => (e.target.src = "/images/avatar.webp")}
                  />
                </div>
                <div className="usher-list-details">
                  <h3>{usher.displayName}</h3>
                  <p className="usher-list-email">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                        fill="currentColor"
                      />
                    </svg>
                    {usher.email || "N/A"}
                  </p>
                  <p className="usher-list-phone">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                        fill="currentColor"
                      />
                    </svg>
                    {usher.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>

              <div className="usher-list-actions">
                <div className="usher-list-status-row">
                  <span className="usher-list-guests-count">
                    Checked In: <strong>{usher.checkedInGuests.length}</strong>
                  </span>
                </div>
                <div className="usher-list-status">
                  {usher.approvalStatus === "confirmed" && (
                    <span className="usher-list-badge usher-list-confirmed">
                      Confirmed
                    </span>
                  )}
                  {usher.approvalStatus === "rejected" && (
                    <span className="usher-list-badge usher-list-rejected">
                      Rejected
                    </span>
                  )}
                  {usher.approvalStatus === "pending" && (
                    <div className="usher-list-pending-container">
                      <span className="usher-list-badge usher-list-awaiting">
                        Awaiting Approval
                      </span>
                      <div className="usher-list-action-buttons">
                        <button
                          className="usher-list-action-btn usher-list-confirm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveUsher(usher);
                            setShowConfirmModal(true);
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          className="usher-list-action-btn usher-list-reject"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveUsher(usher);
                            setShowRejectModal(true);
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="usher-list-pagination">
        <div className="usher-list-rows-per-page">
          <div className="usher-list-select-wrapper">
            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <option value={1}>1</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
            <DropdownArrowIcon />
          </div>
        </div>
        <div className="usher-list-page-info">
          <span>
            {indexOfFirst + 1}-{Math.min(indexOfLast, filteredUshers.length)} of{" "}
            {filteredUshers.length}
          </span>
        </div>
        <div className="usher-list-page-navigation">
          <button
            className="usher-list-prev-btn"
            onClick={() => handlePageChange("prev")}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className="usher-list-next-btn"
            onClick={() => handlePageChange("next")}
            disabled={indexOfLast >= filteredUshers.length}
          >
            Next
          </button>
        </div>
      </div>

      {showConfirmModal && (
        <div className="usher-list-modal-overlay">
          <div className="usher-list-modal">
            <div className="usher-list-icon usher-list-success">✔️</div>
            <h3>Confirm Usher?</h3>
            <p>
              Are you sure you want to confirm{" "}
              <strong>{activeUsher?.displayName}</strong>?
            </p>

            <div className="usher-list-modal-buttons">
              <button
                className="usher-list-cancel-button"
                onClick={() => setShowConfirmModal(false)}
              >
                No, Cancel
              </button>

              <button
                className="usher-list-confirm-button"
                onClick={handleConfirmYes}
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="usher-list-modal-overlay">
          <div className="usher-list-modal">
            <div className="usher-list-icon usher-list-reject">✖️</div>
            <h3>Reject Usher?</h3>
            <p>
              Are you sure you want to reject{" "}
              <strong>{activeUsher?.displayName}</strong>?
            </p>

            <div className="usher-list-modal-buttons">
              <button
                className="usher-list-cancel-button"
                onClick={() => setShowRejectModal(false)}
              >
                No, Cancel
              </button>

              <button
                className="usher-list-confirm-button"
                onClick={handleRejectYes}
              >
                Yes, Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsherList;
